"use client";

import { useLanguageNavigate } from "@/lib/hooks/useLanguageNavigate";
import { useState } from "react";
import { useBackend, useApiError } from "@/lib/hooks";
import { toMotionType, type FrontendEffect } from "@/api/types";
import { DashboardHeader, CreateVideoForm } from "./components";
import { toast } from "@/components/ui/use-toast";
import type { video } from "@/api/client";
import { useTranslation } from "react-i18next";
import type { UploadedImage, FormatImages, VideoFormat } from "@/lib/hooks/useImageUpload";
import type { WatermarkSettings } from "./components/video-form-utils";

/**
 * Convert frontend alignment format (e.g., "bottom-right") to backend format (e.g., "bottom_right")
 * Backend accepts: "top_left" | "top_center" | "top_right" | "center_left" | "center" | "center_right" | "bottom_left" | "bottom_center" | "bottom_right"
 */
function convertAlignment(alignment: string): "top_left" | "top_center" | "top_right" | "center_left" | "center" | "center_right" | "bottom_left" | "bottom_center" | "bottom_right" {
  const mapping: Record<string, "top_left" | "top_center" | "top_right" | "center_left" | "center" | "center_right" | "bottom_left" | "bottom_center" | "bottom_right"> = {
    "top-left": "top_left",
    "top-center": "top_center",
    "top-right": "top_right",
    "center-left": "center_left",
    "center": "center",
    "center-right": "center_right",
    "bottom-left": "bottom_left",
    "bottom-center": "bottom_center",
    "bottom-right": "bottom_right",
  };
  return mapping[alignment] || "bottom_right";
}

interface VideoSubmitData {
  name: string;
  formats: VideoFormat[];
  imagesByFormat: FormatImages;
  watermarkUrl?: string | null;
  watermarkSettingsByFormat: Record<VideoFormat, WatermarkSettings>;
}

function DashboardPage() {
  const navigate = useLanguageNavigate();
  const backend = useBackend();
  const { handleError } = useApiError();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (data: VideoSubmitData) => {
    // Validate that each format has enough images
    for (const format of data.formats) {
      const formatImages = data.imagesByFormat[format] || [];
      if (formatImages.length < 6) {
        toast({
          variant: "destructive",
          title: t("dashboardPage.tooFewImages"),
          description: t("dashboardPage.tooFewImagesForFormat", { format }),
        });
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const totalLength = 30;
      
      // Generate a groupId if more than one format is selected
      const groupId = data.formats.length > 1 ? crypto.randomUUID() : undefined;
      
      // Create a video job for each selected format
      const createdJobs: video.VideoJob[] = [];
      
      for (const format of data.formats) {
        const formatImages: UploadedImage[] = data.imagesByFormat[format] || [];
        
        // Map images to API format
        const images: video.ImageWithMotion[] = formatImages.map((img: UploadedImage) => ({
          url: img.url,
          motion: toMotionType(img.effect as FrontendEffect),
          mediaType: img.mediaType,
        }));
        
        const aspectRatio: video.AspectRatio =
          format === "9:16" ? "9:16" : "16:9";
        
        console.log(`Creating video for format ${format}`, {
          aspectRatio,
          imageCount: images.length,
          urls: images.map(img => img.url),
        });

        const watermarkSettings = data.watermarkSettingsByFormat[format];

        const job = await backend.video.create({
          title: data.name || t("dashboardPage.defaultVideoName"),
          groupId,
          images,
          settings: {
            aspectRatio,
            totalLength,
            upscale: true,
            upscaleResolution: "1080p",
            watermarkImageUrl: data.watermarkUrl || undefined,
            watermarkSize: watermarkSettings?.size,
            watermarkPadding: watermarkSettings?.padding,
            watermarkOpacity: watermarkSettings ? watermarkSettings.opacity / 100 : undefined, // Convert percentage to 0-1
            watermarkAlignment: watermarkSettings ? convertAlignment(watermarkSettings.alignment) : undefined,
            introEntranceAnimation: watermarkSettings?.introAnimation as any,
            introExitAnimation: watermarkSettings?.introAnimation as any,
            introTitle: watermarkSettings?.introTitle,
            introStyle: watermarkSettings?.introStyle as any,
            introBgColor: watermarkSettings?.introBgColor,
            introBgOpacity: watermarkSettings?.introBgOpacity,
            introTextColor: watermarkSettings?.introTextColor,
            introFont: watermarkSettings?.introFont,
            introAlignment: watermarkSettings?.introAlignment ? convertAlignment(watermarkSettings.introAlignment) : undefined,
            introFrameUrls: watermarkSettings?.introFrameUrls,
            logoEnabled: watermarkSettings?.logoEnabled,
            introEnabled: watermarkSettings?.introEnabled,
            contactCard: watermarkSettings?.contactCard,
          },
        });
        
        createdJobs.push(job);
      }

      toast({
        title: t("dashboardPage.videoCreated"),
        description: data.formats.length > 1 
          ? t("dashboardPage.videosCreatedDesc", { count: data.formats.length })
          : t("dashboardPage.videoCreatedDesc"),
      });

      // Navigate to the first video detail page
      if (createdJobs.length > 0 && createdJobs[0]?.id) {
        navigate(`/videos/${createdJobs[0].id}`);
      }
    } catch (error) {
      handleError(error, t("dashboardPage.createError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-lavender overflow-x-clip">
      <main className="max-w-7xl 2k:max-w-[80%] 4k:max-w-[80%] mx-auto px-6 py-8">
        <DashboardHeader />
        <CreateVideoForm onFormSubmit={handleSubmit} isSubmitting={isSubmitting} />
      </main>
    </div>
  );
}

export { DashboardPage };